import { useEffect, useState } from "react";
import { useTheme } from "next-themes";
import { Navigation } from "lucide-react";
import { Pool } from "@/types";
import { Button } from "./ui/button";
import { useAuth } from "../hooks/useAuth";
import { PoolRepository } from "@/infrastructure/repositories/PoolRepository";

interface PoolDetailPageProps {
  id: number;
  onNavigate: (page: string) => void;
}


function openGoogleMapUrl(rawUrl?: string) {
  if (!rawUrl) return;

  const url =
    rawUrl.startsWith("http://") || rawUrl.startsWith("https://")
      ? rawUrl
      : `https://${rawUrl}`;

  window.open(url, "_blank", "noopener,noreferrer");
}


export function PoolDetailPage({ id, onNavigate }: PoolDetailPageProps) {
  const { theme } = useTheme();
  const { clearError } = useAuth();
  const logoSrc = theme === "dark" ? "/logo_white.png" : "/logo.png";

  const poolRepo = new PoolRepository();

  const [pool, setPool] = useState<Pool | null>(null);
  const [loading, setLoading] = useState(true);
  
  const navigate = (url: string, page: string, onNavigate: (p: string) => void) => {
    window.history.pushState({}, "", url);
    onNavigate(page);
  };


  const toSlug = (text: string) =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)+/g, "");

  // ----------------------------------
  // OLD FLOW: FETCH LIST → FIND BY ID
  // ----------------------------------
  useEffect(() => {
    const loadPoolFromList = async () => {
      setLoading(true);

      // Large page size to mimic old behavior
      const pools = await poolRepo.getPoolList(1, 1000);

      const found = pools.find(p => p.id === id) ?? null;
      setPool(found);

      setLoading(false);
    };

    loadPoolFromList();
  }, [id]);

   useEffect(() => {
    if (!pool) return;

    const slug = toSlug(pool.poolname);
    const correctUrl = `/pool/${pool.id}/${slug}`;

    if (window.location.pathname !== correctUrl) {
      window.history.replaceState({}, "", correctUrl);
    }
  }, [pool]);
 
  // ----------------------------------
  // UI STATES
  // ----------------------------------
  if (loading) {
    return (
          <div className="min-h-screen flex items-center justify-center text-muted-foreground">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>  
          </div>
    );
  }

  if (!pool) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        Pool not found
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* HEADER */}
      <header className="border-b sticky top-0 z-50 bg-white/80 backdrop-blur">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <img
            src={logoSrc}
            onClick={() => navigate("/" , "landing" , onNavigate)}
            alt="Swimnerd Logo"
            className="h-12 cursor-pointer"
          />

          <div className="flex gap-4">
            <Button
              variant="ghost"
              onClick={() => {
                clearError();
                navigate("/find-pool", "find-pool", onNavigate);
              }}
            >
              Find Pool
            </Button>

            <Button
              variant="ghost"
              onClick={() => {
                clearError();
                navigate("/blog", "blog", onNavigate);
              }}
            >
              Blog
            </Button>

            <Button
              variant="ghost"
              onClick={() => {
                clearError();
                navigate("/signin", "signin", onNavigate);
              }}
            >
              Sign In
            </Button>

            <Button
              onClick={() => {
                clearError();
                navigate("/signup", "signup", onNavigate);
              }}
            >
              Get Started
            </Button>
          </div>


          {/* <div className="flex gap-4">
            <Button
              variant="ghost"
              onClick={() => {
                clearError();
                onNavigate("find-pool");
              }}
            >
              Find Pool
            </Button>

            <Button
              variant="ghost"
              onClick={() => {
                clearError();
                onNavigate("blog");
              }}
            >
              Blog
            </Button>

            <Button
              variant="ghost"
              onClick={() => {
                clearError();
                onNavigate("signin");
              }}
            >
              Sign In
            </Button>

            <Button
              onClick={() => {
                clearError();
                onNavigate("signup");
              }}
            >
              Get Started
            </Button>
          </div> */}
        </div>
      </header>

      {/* HERO */}
      <section className="relative">
        <div className="h-[520px] w-full overflow-hidden">
          <img
            src={pool.image}
            alt={pool.poolname}
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent" />
        </div>

        <div className="absolute inset-0 flex items-end">
          <div className="max-w-4xl mx-auto px-6 pb-16 text-white">
            <div className="flex items-center gap-3 mb-4">
              <span className="rounded-full bg-white/20 px-4 py-1 text-xs font-semibold backdrop-blur">
                {pool.type || "Pool"}
              </span>

              {pool.is_verified && (
                <span className="rounded-full bg-green-500/80 px-3 py-1 text-xs font-semibold">
                  Verified
                </span>
              )}
            </div>

            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              {pool.poolname}
            </h1>

            <p className="text-lg text-white/90 mb-6">
              {pool.city}, {pool.state}
            </p>

            <div className="flex flex-wrap items-center gap-6 text-sm">
              <div>
                <span className="font-medium">Timings:</span>{" "}
                {pool.timing || "—"}
              </div>

              {pool.googlemap_url && (
                <button
                  onClick={() => openGoogleMapUrl(pool.googlemap_url)}
                  className="inline-flex items-center gap-2 rounded-full bg-white/20 px-4 py-2 hover:bg-white/30"
                >
                  <Navigation className="h-4 w-4" />
                  Directions
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* BODY */}
      <main className="bg-gray-50">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            <InfoCard
              label="Pool Length"
              value={pool.pool_length_m ? `${pool.pool_length_m} m` : "—"}
            />
            <InfoCard label="Depth" value={pool.depth_m ?? "—"} />
            <InfoCard
              label="Price / Session"
              value={pool.price_per_session ? `₹${pool.price_per_session}` : "—"}
            />
            <InfoCard
              label="Rating"
              value={pool.rating ? `${pool.rating} / 5` : "—"}
            />
          </div>

          {pool.about && (
            <Section title={`About ${pool.poolname}`}>
              <div
                className="text-gray-700 leading-8 max-w-3xl"
                dangerouslySetInnerHTML={{ __html: pool.about }}
              />
            </Section>
          )}

          {pool.amenities && pool.amenities.length > 0 && (
            <Section title="Facilities & Amenities">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-3xl">
                {pool.amenities.map(item => (
                  <div key={item} className="flex items-center gap-3">
                    <span className="h-2.5 w-2.5 rounded-full bg-blue-600" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </Section>
          )}
        </div>
      </main>
    </div>
  );
}

function InfoCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-white rounded-2xl border p-6">
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <p className="text-lg font-semibold">{value}</p>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white rounded-3xl border p-10 mb-16">
      <h2 className="text-2xl font-semibold mb-4">{title}</h2>
      {children}
    </div>
  );
}
